import { Marked } from "marked";
import { MarkdownManager } from "@tiptap/markdown";

/**
 * Mixla stores descriptions, steps, notes and comments as GitHub-flavoured Markdown, with a single
 * newline kept as a line break (`breaks`), as GitHub comments do. The renderer (`RichText`) and the
 * editor (`MarkdownEditor`) both parse with marked under these options, so they agree on what a
 * piece of text means; the CLI and agents write the same Markdown.
 */
export const MARKED_OPTIONS = { gfm: true, breaks: true } as const;

export const marked = new Marked(MARKED_OPTIONS);

/** Links we render as links: http(s), mailto, and in-app paths. Anything else stays text. */
export const SAFE_URL = /^(https?:\/\/|mailto:|\/(?!\/))/i;

/** `@username`, not inside a word, an email or a path. Matches the server's `chat::mentions`. */
export const MENTION = /(?<![\w@./-])@([A-Za-z0-9][\w-]*(?:\.[\w-]+)*)/;

/**
 * Backslash-escapes only what would otherwise read as Markdown, so text the editor saves stays
 * close to what was typed. Tiptap's default escapes every `_`, `[`, `]`, `<` and `&`, which turns
 * `@john_doe` into `@john\_doe` (the server would then mention `john`) and `a < b` into `a &lt; b`.
 * - `\` only before punctuation; `` ` ``, `*` and `~` always (they open code, emphasis, strike).
 * - `_` only at a word edge: GFM never reads an underscore inside a word as emphasis.
 * - `[` only where a link (`[x](`, `[x][`) would follow; `<` only before a tag; `&` only before an entity.
 */
export function escapeMarkdownText(text: string): string {
  return text
    .replace(/\\(?=[!-/:-@[-`{-~])/g, "\\\\")
    .replace(/[`*~]/g, "\\$&")
    .replace(/(?<![\p{L}\p{N}])_|_(?![\p{L}\p{N}])/gu, "\\_")
    .replace(/\[(?=[^\]]*\][([])/g, "\\[")
    .replace(/<(?=[A-Za-z/!?])/g, "\\<")
    .replace(/&(?=#?\w+;)/g, "\\&");
}

// Tiptap has no option for its text escaping, so the editor's serializer is replaced here. Pinned
// to @tiptap/markdown 3.31.4: check this still overrides the method when upgrading.
type Manager = {
  codeTypes: Set<string>;
  encodeTextForMarkdown: (text: string, node: { marks?: unknown[] }, parent?: { type?: string }) => string;
};
const proto = MarkdownManager.prototype as unknown as Manager;
if (typeof proto.encodeTextForMarkdown !== "function") {
  console.warn("@tiptap/markdown changed: Mixla's Markdown escaping is not applied");
}
proto.encodeTextForMarkdown = function (this: Manager, text, node, parent) {
  const inCode =
    (parent?.type != null && this.codeTypes.has(parent.type)) ||
    (node.marks ?? []).some((m) => this.codeTypes.has(typeof m === "string" ? m : (m as { type: string }).type));
  return inCode ? text : escapeMarkdownText(text);
};
